import React from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, Eye, FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen relative">
      <SEO
        title="Privacy Policy | Emerge Automations"
        description="Learn how Emerge Automations handles your data and ensures privacy in the age of AI. Our commitment to data security and transparency."
        canonical="https://emergeautomations.com/privacy-policy/"
      />

      <div className="container-custom pt-32 md:pt-40 pb-24 relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          Back to home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <span className="eyebrow mb-6 inline-block">Legal</span>
          <div className="mb-8 h-px bg-white/10" />
          <h1 className="text-[clamp(2.25rem,5vw,4rem)] font-display font-semibold text-white leading-[1.05] tracking-[-0.035em] mb-6">
            Privacy Policy
          </h1>
          <p className="text-[17px] text-zinc-400 leading-[1.65] mb-16">
            Last Updated: April 8, 2026. Your privacy is paramount. This policy outlines how we collect, use, and protect your information when you interact with Emerge Automations.
          </p>

          <div className="space-y-14 prose-emerge">
            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <Shield className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                1. Overview
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75]">
                Emerge Automations ("we," "our," or "us") provides AI automation services, including voice agents, workflow automation, and strategic consulting. We are committed to protecting the personal data of our clients and their end-users.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <Eye className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                2. Information We Collect
              </h2>
              <div className="space-y-3 text-[16px] text-zinc-300 leading-[1.75]">
                <p>
                  <strong>Personal Information:</strong> We collect names, email addresses, and phone numbers when you contact us or sign up for our services.
                </p>
                <p>
                  <strong>Business Data:</strong> To provide automation services, we may process business-related data, including CRM records, workflow logs, and internal documentation provided by you.
                </p>
                <p>
                  <strong>Usage Data:</strong> We collect information on how you interact with our website and AI tools to improve our services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <Lock className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                3. AI Data Processing &amp; Security
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75] mb-3">
                When implementing AI solutions (such as Custom LLMs or Voice Agents), we adhere to strict data security protocols:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[16px] text-zinc-300 leading-[1.75]">
                <li>
                  <strong>Data Isolation:</strong> Your business data used for training custom AI models is isolated and never used to train public models (e.g., public versions of GPT).
                </li>
                <li>
                  <strong>Encryption:</strong> All data in transit and at rest is encrypted using industry-standard protocols.
                </li>
                <li>
                  <strong>Anonymization:</strong> Where possible, we encourage and implement PII (Personally Identifiable Information) stripping before data is processed by AI models.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <FileText className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                4. How We Use Your Information
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-[16px] text-zinc-300 leading-[1.75]">
                <li>To deliver and maintain our AI automation services.</li>
                <li>To communicate with you regarding project updates and support.</li>
                <li>To analyze and improve the performance of our AI agents and workflows.</li>
                <li>To comply with legal obligations in the UK and USA.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 tracking-[-0.01em]">
                5. Third-Party Services
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75]">
                We use trusted third-party providers to power our automations, including but not limited to OpenAI, Anthropic, n8n, and Google Cloud. These providers have their own privacy policies and data processing agreements which we review to ensure compliance with our standards.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 tracking-[-0.01em]">
                6. Your Rights
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75]">
                Depending on your location (GDPR in the UK/EU, CCPA in the USA), you have rights regarding your personal data, including the right to access, correct, or delete your information. To exercise these rights, please contact us at{' '}
                <a href="mailto:privacy@emergeautomations.com" className="text-[color:var(--color-accent)] hover:underline">
                  privacy@emergeautomations.com
                </a>.
              </p>
            </section>

            <section className="p-6 md:p-8 rounded-[var(--radius-lg)] border border-white/10 bg-[color:var(--color-surface-1)]">
              <h2 className="text-lg font-semibold text-white mb-2">Contact Our Privacy Team</h2>
              <p className="text-sm text-zinc-400 leading-relaxed">
                If you have questions about this policy or our data practices, please reach out to our Data Protection Officer at{' '}
                <a href="mailto:emergeautomations@gmail.com" className="text-[color:var(--color-accent)] hover:underline">
                  emergeautomations@gmail.com
                </a>.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
